import { isEmpty, set } from 'lodash'
import { useMemo } from 'react'
import { adapters } from 'utils/documents'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import { STORAGE_KEY } from '../constants'
import { selectUserId } from '../selectors'

const getStorage = () => JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}

const makeColumnVisibilityHandler =
  ({ label, userId, exclude = [] }) =>
  ({ visible, columns }) => {
    const hiddenColumnsStorage = getStorage()

    const colIds = columns
      .map(({ colId }) => colId)
      .filter((id) => !exclude.includes(id))
    const userHiddenColumns = hiddenColumnsStorage[userId]?.[label] || []
    const hiddenColumns = visible
      ? userHiddenColumns.filter((col) => !colIds.includes(col))
      : [...userHiddenColumns, ...colIds]

    if (hiddenColumns.length > 0) {
      set(hiddenColumnsStorage, [userId, label], hiddenColumns)
    } else {
      delete hiddenColumnsStorage[userId]?.[label]
      if (isEmpty(hiddenColumnsStorage[userId])) {
        delete hiddenColumnsStorage[userId]
      }
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(hiddenColumnsStorage))
  }

const makeIsColumnHidden =
  ({ label, userId }) =>
  (colDef) => {
    const hiddenColumnsStorage = getStorage()
    const colId = adapters.getId(colDef)
    return hiddenColumnsStorage[userId]?.[label]?.includes(colId)
  }

export default function useColumnVisibility({ label, exclude }) {
  const userId = useStoreSelector('user', selectUserId)

  const methods = useMemo(() => {
    const params = { label, userId, exclude }
    return {
      columnVisibilityHandler: makeColumnVisibilityHandler(params),
      isColumnHidden: makeIsColumnHidden(params)
    }
  }, [label, userId])

  return methods
}
